import type { TokenType } from '@components/transfer/TokenSelectSheet'
import { formatEthDisplay, formatWithCommas } from './format'

/**
 * 推定ガス代(wei)にマージンを加算し、バリデーション・表示・トランザクションで
 * 共通利用する「必要手数料(wei)」を算出する。
 *
 * - ARCS: 固定手数料分はマージン対象外、ガス部分のみにマージン適用
 * - ETH:  全額にマージン適用
 */
export function computeRequiredFeeWithMargin(params: {
  estimatedFeeWei: bigint | null
  feeToken: TokenType
  paymasterConstantFeeWei: bigint
  feeMarginPercent: number
}): bigint {
  const { estimatedFeeWei, feeToken, paymasterConstantFeeWei, feeMarginPercent } = params
  const feeAmountRaw = estimatedFeeWei ?? 0n

  if (feeToken === 'arcs') {
    const gasPart =
      feeAmountRaw > paymasterConstantFeeWei ? feeAmountRaw - paymasterConstantFeeWei : 0n
    const multiplier = 100 + feeMarginPercent
    return paymasterConstantFeeWei + (gasPart * BigInt(multiplier)) / 100n
  }

  if (feeToken === 'ethereum' && feeAmountRaw > 0n) {
    return (feeAmountRaw * BigInt(100 + feeMarginPercent)) / 100n
  }

  return feeAmountRaw
}

/**
 * requiredFeeWeiWithMargin(wei) を表示用文字列に変換する。
 * 表示・バリデーション・トランザクションすべてが同じ wei 値から導出されるため
 * 食い違いが起きない。
 */
export function formatFeeDisplay(requiredFeeWeiWithMargin: bigint): string {
  if (requiredFeeWeiWithMargin === 0n) return '0'
  return formatWithCommas(formatEthDisplay(Number(requiredFeeWeiWithMargin) / 1e18))
}

/**
 * 残高が「送金額 + 手数料」を賄えるか判定する。
 *
 * ARCS と ETH それぞれで「送金額 + 手数料」を積算し、残高と比較する。
 * 手数料が requiredFeeWeiWithMargin（マージン込み）で統一されているため、
 * 表示と食い違わない。
 */
export function checkSufficientBalance(params: {
  amount: string
  sendToken: TokenType
  feeToken: TokenType
  requiredFeeWeiWithMargin: bigint
  arcsBalance: bigint
  ethBalance: bigint
  parseUnits: (value: string, decimals: number) => bigint
}): boolean {
  const {
    amount,
    sendToken,
    feeToken,
    requiredFeeWeiWithMargin,
    arcsBalance,
    ethBalance,
    parseUnits,
  } = params

  if (!amount || Number(amount) <= 0) return false

  try {
    const amountValue = parseUnits(amount, 18)

    const requiredArx = sendToken === 'arcs' ? amountValue : 0n
    const requiredArxForFee = feeToken === 'arcs' ? requiredFeeWeiWithMargin : 0n
    const requiredEth = sendToken === 'ethereum' ? amountValue : 0n
    const requiredEthForFee = feeToken === 'ethereum' ? requiredFeeWeiWithMargin : 0n

    return (
      arcsBalance >= requiredArx + requiredArxForFee &&
      ethBalance >= requiredEth + requiredEthForFee
    )
  } catch {
    return false
  }
}

/**
 * ARCS 手数料かつ残高ギリギリのとき注意メッセージを出すか判定する。
 * (手数料の 20% 未満の余裕しかない場合)
 */
export function isBalanceTightForArxFee(params: {
  amount: string
  sendToken: TokenType
  feeToken: TokenType
  requiredFeeWeiWithMargin: bigint
  arcsBalance: bigint
  parseUnits: (value: string, decimals: number) => bigint
}): boolean {
  const { amount, sendToken, feeToken, requiredFeeWeiWithMargin, arcsBalance, parseUnits } = params

  if (feeToken !== 'arcs' || !amount || Number(amount) <= 0) return false

  try {
    const amountValue = parseUnits(amount, 18)
    const requiredArx = sendToken === 'arcs' ? amountValue : 0n
    const requiredTotal = requiredArx + requiredFeeWeiWithMargin
    if (arcsBalance < requiredTotal) return false
    const buffer = arcsBalance - requiredTotal
    const tightThreshold = requiredFeeWeiWithMargin / 5n
    return buffer < tightThreshold
  } catch {
    return false
  }
}
